import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, X, Search } from 'lucide-react';
import api from '../../services/api';

const MultiUserSelect = ({
  selectedUsers = [],
  onChange,
  excludeUserIds = [],
  placeholder = "Search for users...",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedIds = new Set(selectedUsers.map(u => u.id));
  const excludeIds = new Set(excludeUserIds);

  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await api.get('/users/firm-users/autocomplete', {
          params: { query: searchQuery, limit: 20 }
        });
        const filtered = (response.data || []).filter(
          u => !selectedIds.has(u.id) && !excludeIds.has(u.id)
        );
        setResults(filtered);
      } catch (error) {
        console.error('Error fetching users:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchUsers, 150);
    return () => clearTimeout(timer);
  }, [isOpen, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdd = useCallback((user) => {
    onChange([...selectedUsers, user]);
    setSearchQuery('');
    inputRef.current?.focus();
  }, [selectedUsers, onChange]);

  const handleRemove = useCallback((userId) => {
    onChange(selectedUsers.filter(u => u.id !== userId));
  }, [selectedUsers, onChange]);

  return (
    <div ref={containerRef} className="relative">
      {/* Selected users as chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedUsers.map(user => (
            <span
              key={user.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded-full text-sm text-gray-800"
            >
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-4 h-4 rounded-full" />
              ) : (
                <User className="w-3.5 h-3.5 text-gray-500" />
              )}
              <span className="max-w-[150px] truncate">{user.display_name || `${user.first_name} ${user.last_name}`}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(user.id)}
                  className="p-0.5 hover:bg-yellow-200 rounded-full"
                >
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      {!disabled && (
        <div
          onClick={() => { setIsOpen(true); inputRef.current?.focus(); }}
          className={`flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 cursor-text bg-white
            ${isOpen ? 'ring-2 ring-yellow-500 border-transparent' : 'hover:border-gray-400'}`}
        >
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); if (!isOpen) setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="flex-1 outline-none bg-transparent text-sm text-gray-900 placeholder-gray-400"
          />
        </div>
      )}

      {/* Dropdown results */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-[200px] overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchQuery ? 'No users found' : 'Type to search'}
            </div>
          ) : (
            results.map(user => (
              <button
                key={user.id}
                type="button"
                onClick={() => handleAdd(user)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{user.display_name}</div>
                  <div className="text-xs text-gray-500 truncate">{user.email}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MultiUserSelect;
